import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { CreativeItem } from '@/entities/creatives';
import { CreativeItemType, selectCreativeByTuple } from '@/entities/creatives';
import { useAppSelector } from '@/shared/model';
import { CopyButton, Panel, Scroll } from '@/shared/ui';
import { useCreativeDrawerContext } from './CreativeDrawerContext';
import { ImageCarousel } from './ImageCarousel';
import { VideoCarousel } from './VideoCarousel';

const DRAWER_WIDTH = 340;

const TYPE_LABELS: Record<CreativeItemType, string> = {
  [CreativeItemType.Image]: 'Image',
  [CreativeItemType.Video]: 'Video',
  [CreativeItemType.Carousel]: 'Carousel',
  [CreativeItemType.DynamicImage]: 'Dynamic Image',
  [CreativeItemType.DynamicVideo]: 'Dynamic Video',
  [CreativeItemType.Unknown]: 'Unknown',
};

function CopyField({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 0.5, display: 'block' }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'action.hover',
          px: 1.5,
          py: 1,
          borderRadius: 1,
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{ flex: 1, fontFamily: 'monospace', fontSize: '0.8rem' }}
        >
          {value}
        </Typography>
        <CopyButton copyContent={value} />
      </Box>
    </Box>
  );
}

function renderMedia(item: CreativeItem) {
  switch (item.type) {
    case CreativeItemType.DynamicVideo:
    case CreativeItemType.Video:
      return (
        <VideoCarousel
          videoPreviewUrl={item.videoPreviewUrl}
          assets={item.assets}
          resetKey={item.adId}
        />
      );
    case CreativeItemType.Carousel:
    case CreativeItemType.DynamicImage:
    case CreativeItemType.Image:
      return <ImageCarousel assets={item.assets} resetKey={item.adId} />;
    case CreativeItemType.Unknown:
    default:
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Unknown creative type. We are working to display it.
          </Typography>
        </Box>
      );
  }
}

function DrawerContent({ creative }: { readonly creative: CreativeItem }) {
  const { closeCreativeDrawer } = useCreativeDrawerContext();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          p: 2,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{
            pr: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {creative.adName}
        </Typography>
        <IconButton
          size="small"
          onClick={closeCreativeDrawer}
          sx={{ mt: -0.5 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Badges */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          pb: 1.5,
          flexShrink: 0,
        }}
      >
        <Chip label={creative.sourceName} size="small" />
        <Typography variant="caption" color="text.secondary">
          {TYPE_LABELS[creative.type] ?? 'Unknown'}
        </Typography>
      </Box>

      {/* Scrollable middle: media + fields */}
      <Scroll
        slotProps={{
          container: {
            sx: { px: 2, display: 'flex', flexDirection: 'column', gap: 2 },
          },
        }}
      >
        <Box>{renderMedia(creative)}</Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 2 }}>
          <CopyField label="Creative ID" value={creative.creativeId} />
          <CopyField label="Creative name" value={creative.creativeName} />
        </Box>
      </Scroll>
    </Box>
  );
}

export function CreativeDetailsDrawer({
  sx,
}: {
  readonly sx?: SxProps<Theme>;
}) {
  const { currentTuple } = useCreativeDrawerContext();
  const creative = useAppSelector(
    selectCreativeByTuple(
      currentTuple ?? { ad_id: '', ad_name: '', media_source: '' },
    ),
  );
  const open = creative != null;

  return (
    <Panel
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        ml: open ? 1 : 0,
        flexShrink: 0,
        visibility: open ? 'visible' : 'hidden',
        transition: 'width 0.2s ease-in-out, margin 0.2s ease-in-out',
        display: 'flex',
        flexDirection: 'column',

        '& .drawer-content': {
          opacity: open ? 1 : 0,
          transition: open ? 'opacity 0s ease-in-out 0.2s' : 'none',
          height: '100%',
        },
        ...sx,
      }}
    >
      <Box className="drawer-content">
        {creative && <DrawerContent creative={creative} />}
      </Box>
    </Panel>
  );
}
